import React, { useRef, useEffect } from "react";
import { Box, Typography, Container, Grid, useMediaQuery, useTheme } from "@mui/material";
import { motion, useInView, useAnimation } from "framer-motion";
import gymCheckinVideo from "./gym.mp4"; // Import the video file

// AI capabilities list component
const FeatureItem = ({ icon, title, description }) => {
  return (
    <Box
      component={motion.div}
      whileHover={{ y: -5 }}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        p: 3,
        borderRadius: "16px",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        transition: "all 0.3s ease",
        height: "100%",
        "&:hover": {
          background: "rgba(155, 90, 242, 0.1)",
          borderColor: "rgba(155, 90, 242, 0.3)",
        },
      }}
    >
      <Box 
        sx={{ 
          color: "#9b5af2", 
          fontSize: "2rem", 
          mb: 2,
        }}
      >
        {icon}
      </Box>
      <Typography 
        variant="h6" 
        sx={{ 
          mb: 1.5, 
          fontWeight: "600",
          color: "#fff"
        }}
      >
        {title}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{ 
          color: "rgba(255, 255, 255, 0.7)",
          lineHeight: 1.6,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
};

const AiVerificationSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, threshold: 0.2 });
  
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <Box
      ref={ref}
      sx={{
        py: { xs: 12, md: 16 },
        position: "relative",
        overflow: "hidden",
        color: "#fff",
      }}
    >
      {/* Background with gradient and particle effect */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, #1a1a35 0%, #2d2d5f 50%, #1a1a35 100%)`,
          zIndex: -2,
        }}
      />
      
      {/* Animated gradient orbs in background */}
      <Box
        component={motion.div}
        animate={{
          y: ["-5%", "5%"],
          x: ["3%", "-3%"],
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 15,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: "40vw",
          height: "40vw",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(155, 90, 242, 0.2) 0%, rgba(155, 90, 242, 0) 70%)",
          filter: "blur(60px)",
          top: "-10%",
          right: "-5%",
          zIndex: -1,
        }}
      />
      
      <Box
        component={motion.div}
        animate={{
          y: ["5%", "-5%"],
          x: ["-2%", "2%"],
        }}
        transition={{
          repeat: Infinity,
          repeatType: "reverse",
          duration: 18,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: "30vw",
          height: "30vw",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30, 144, 255, 0.15) 0%, rgba(30, 144, 255, 0) 70%)",
          filter: "blur(60px)",
          bottom: "-10%",
          left: "20%",
          zIndex: -1,
        }}
      />

      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {/* Section Header */}
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <motion.div variants={itemVariants}>
              <Typography
                variant="overline"
                sx={{
                  color: "#9b5af2",
                  fontSize: "0.9rem",
                  letterSpacing: 2,
                  mb: 1,
                  display: "block",
                  fontWeight: 600,
                }}
              >
                POWERED BY ADVANCED NEURAL NETWORKS
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant="h2"
                sx={{
                  mb: { xs: 3, md: 4 },
                  fontWeight: 800,
                  fontSize: { xs: "2rem", md: "3.2rem" },
                  background: "linear-gradient(to right, #ffffff, #9b5af2)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  display: "inline-block",
                }}
              >
                AI Environment Verification
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { xs: "1rem", md: "1.25rem" },
                  maxWidth: "800px",
                  color: "rgba(255, 255, 255, 0.8)",
                  lineHeight: 1.7,
                  mx: "auto",
                  mb: { xs: 4, md: 1 },
                }}
              >
                Our computer vision technology uses advanced AI to verify you're in the right place
                at the right time, making your streak tracking seamless and cheat-proof.
              </Typography>
            </motion.div>
          </Box>

          {/* Content Grid */}
          <Grid container spacing={4} alignItems="center">
            {/* Left side - Video */}
            <Grid item xs={12} md={5} sx={{ order: { xs: 2, md: 1 } }}>
              <motion.div
                variants={itemVariants}
                style={{ display: "flex", justifyContent: "center" }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: { xs: "240px", md: "280px" },
                  }}
                >
                  {/* Phone frame */}
                  <Box
                    component={motion.div}
                    whileHover={{ y: -5 }}
                    sx={{
                      width: "100%",
                      aspectRatio: "480 / 1040",
                      background: "#121212",
                      borderRadius: "32px",
                      padding: "12px",
                      boxShadow: "0 30px 60px rgba(0, 0, 0, 0.4), 0 0 40px rgba(155, 90, 242, 0.2)",
                      border: "2px solid rgba(70, 70, 100, 0.6)",
                      overflow: "hidden",
                      position: "relative",
                    }}
                  >
                    {/* Video */}
                    <Box
                      component="video"
                      src={gymCheckinVideo}
                      autoPlay
                      muted
                      loop
                      playsInline
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "24px",
                      }}
                    />
                    
                    {/* Verification success overlay */}
                    <Box
                      component={motion.div}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1, 1, 0] }}
                      transition={{
                        duration: 3,
                        repeat: Infinity,
                        repeatDelay: 4,
                      }}
                      sx={{
                        position: "absolute",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        background: "rgba(0, 0, 0, 0.7)",
                        backdropFilter: "blur(8px)",
                        borderRadius: "12px",
                        py: 1.5,
                        px: 2.5,
                        width: "80%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px solid rgba(155, 90, 242, 0.3)",
                      }}
                    >
                      <Box
                        component={motion.div}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.5 }}
                        sx={{
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          mr: 1.5,
                          background: "#4CAF50",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box
                          component="svg"
                          sx={{ width: "12px", height: "12px", fill: "white" }}
                          viewBox="0 0 24 24"
                        >
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600 }}>
                        Gym Environment Verified
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Reflection effect */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "40%",
                      background: "linear-gradient(to bottom, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))",
                      borderTopLeftRadius: "32px",
                      borderTopRightRadius: "32px",
                      pointerEvents: "none",
                    }}
                  />
                </Box>
              </motion.div>
            </Grid>
            
            {/* Right side - Features */}
            <Grid item xs={12} md={7} sx={{ order: { xs: 1, md: 2 } }}>
              <Box sx={{ pl: { xs: 0, md: 4 } }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <motion.div variants={itemVariants}>
                      <FeatureItem
                        icon={<span>🏋️</span>}
                        title="Smart Gym Detection"
                        description="Our AI can identify over 1,000 gym environments, verifying your workout location instantly with 99.7% accuracy."
                      />
                    </motion.div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <motion.div variants={itemVariants}>
                      <FeatureItem
                        icon={<span>🔍</span>}
                        title="Fraud Prevention"
                        description="Advanced algorithms detect fake environments and old photos, ensuring all streaks are legitimate."
                      />
                    </motion.div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <motion.div variants={itemVariants}>
                      <FeatureItem
                        icon={<span>🏢</span>}
                        title="Multi-Context Support"
                        description="Tracks locations for various activities: gyms, libraries, studios, offices, and more."
                      />
                    </motion.div>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <motion.div variants={itemVariants}>
                      <FeatureItem
                        icon={<span>🔐</span>}
                        title="Privacy-Focused"
                        description="Image processing happens on-device with zero persistent data storage for maximum privacy."
                      />
                    </motion.div>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
};

export default AiVerificationSection;